import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/authFetch';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material';
import {
  ExpandMore,
  Search,
  UnfoldMore,
  UnfoldLess,
  Warning,
  CheckCircle,
  Person,
  CalendarToday,
  FilterAlt,
  Refresh,
} from '@mui/icons-material';
import { format, parseISO, isPast } from 'date-fns';
import { JIRA } from '../config/api';

// Type definitions based on API response
interface JiraIssue {
  key: string;
  title: string;
  type: string;
  status: string;
  assignee: string | null;
  start_date: string | null;
  due_date: string | null;
}

interface JiraEpic {
  key: string;
  title: string;
  status: string;
  assignee: string | null;
  start_date?: string | null;
  due_date?: string | null;
  issues: JiraIssue[];
}

interface JiraCapability {
  key: string;
  title: string;
  status: string;
  epics: JiraEpic[];
}

interface JiraHierarchyResponse {
  count: number;
  data: JiraCapability[];
}

type JiraHierarchyData = JiraCapability[];

export const JiraActions: React.FC = () => {
  // State management
  const [data, setData] = useState<JiraHierarchyData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCapabilities, setExpandedCapabilities] = useState<Set<string>>(new Set());
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(new Set());
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  
  // Expand/collapse all state
  const [expandAll, setExpandAll] = useState<boolean>(false);

  // Fetch data from API
  const fetchJiraHierarchy = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(JIRA.HIERARCHY);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const jsonResponse: JiraHierarchyResponse = await response.json();
      setData(jsonResponse.data);
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch Jira hierarchy data');
      console.error('Error fetching Jira hierarchy:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJiraHierarchy();
  }, []);

  // Helper functions
  const getStatusColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('done')) return '#4caf50'; // Green
    if (statusLower.includes('in progress')) return '#1976d2'; // Blue
    if (statusLower.includes('to do')) return '#757575'; // Gray
    if (statusLower.includes('blocked')) return '#1a1a1a'; // Dark/Black
    if (statusLower.includes('ready for validation')) return '#9c27b0'; // Purple
    if (statusLower.includes('on hold')) return '#ff9800'; // Orange
    if (statusLower.includes('backlog')) return '#9e9e9e'; // Light Gray
    if (statusLower.includes('removed')) return '#f44336'; // Red
    if (statusLower.includes('selected for development')) return '#bdbdbd'; // Medium Gray
    return '#757575'; // Default Gray
  };

  const getStatusTextColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    // Light backgrounds need dark text
    if (statusLower.includes('selected for development')) return '#000000';
    if (statusLower.includes('backlog')) return '#000000';
    // All other colors are dark enough for white text
    return '#ffffff';
  };

  const getTypeIcon = (type: string): string => {
    // Return empty string - no emojis needed
    return '';
  };

  const getJiraUrl = (key: string): string => {
    return `https://maersk-tools.atlassian.net/browse/${key}`;
  };

  const isOverdue = (dueDate: string | null): boolean => {
    if (!dueDate) return false;
    try {
      return isPast(parseISO(dueDate));
    } catch {
      return false;
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const getInitials = (name: string | null): string => {
    if (!name) return 'UA';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Calculate statistics
  const getEpicStats = (epic: JiraEpic) => {
    const total = epic.issues.length;
    const done = epic.issues.filter(i => i.status.toLowerCase().includes('done')).length;
    const inProgress = epic.issues.filter(i => i.status.toLowerCase().includes('progress')).length;
    const toDo = total - done - inProgress;
    const progress = total > 0 ? Math.round((done / total) * 100) : 0;
    
    return { total, done, inProgress, toDo, progress };
  };

  const getCapabilityStats = (capability: JiraCapability) => {
    const totalEpics = capability.epics.length;
    const allIssues = capability.epics.flatMap(e => e.issues);
    const totalIssues = allIssues.length;
    const doneIssues = allIssues.filter(i => i.status.toLowerCase().includes('done')).length;
    const progress = totalIssues > 0 ? Math.round((doneIssues / totalIssues) * 100) : 0;
    
    return { totalEpics, totalIssues, doneIssues, progress };
  };

  // Get unique assignees for filter
  const getUniqueAssignees = (): string[] => {
    if (!data) return [];
    const assignees = new Set<string>();
    data.forEach(cap => {
      cap.epics.forEach(epic => {
        if (epic.assignee) assignees.add(epic.assignee);
        epic.issues.forEach(issue => {
          if (issue.assignee) assignees.add(issue.assignee);
        });
      });
    });
    return Array.from(assignees).sort();
  };

  // Filter logic
  const filterData = (): JiraCapability[] => {
    if (!data) return [];
    
    // If no filters are active, return all data
    const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || assigneeFilter !== 'all';
    
    if (!hasActiveFilters) {
      return data;
    }
    
    // Apply filters
    return data
      .map(capability => ({
        ...capability,
        epics: capability.epics
          .map(epic => ({
            ...epic,
            issues: epic.issues.filter(issue => {
              // Search filter - normalize both search and data to uppercase for comparison
              const searchUpper = searchQuery.toUpperCase();
              const matchesSearch = searchQuery === '' || 
                issue.title.toUpperCase().includes(searchUpper) ||
                issue.key.toUpperCase().includes(searchUpper) ||
                epic.title.toUpperCase().includes(searchUpper) ||
                epic.key.toUpperCase().includes(searchUpper) ||
                capability.title.toUpperCase().includes(searchUpper) ||
                capability.key.toUpperCase().includes(searchUpper);
              
              // Status filter
              const matchesStatus = statusFilter === 'all' || issue.status === statusFilter;
              
              // Assignee filter
              const matchesAssignee = assigneeFilter === 'all' || issue.assignee === assigneeFilter;
              
              return matchesSearch && matchesStatus && matchesAssignee;
            })
          }))
          .filter(epic => epic.issues.length > 0)
      }))
      .filter(capability => capability.epics.length > 0);
  };

  // Toggle functions
  const toggleCapability = (key: string) => {
    setExpandedCapabilities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const toggleEpic = (key: string) => {
    setExpandedEpics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleExpandAll = () => {
    if (expandAll) {
      setExpandedCapabilities(new Set());
      setExpandedEpics(new Set());
    } else {
      const filteredData = filterData();
      setExpandedCapabilities(new Set(filteredData.map(c => c.key)));
      setExpandedEpics(new Set(filteredData.flatMap(c => c.epics.map(e => e.key))));
    }
    setExpandAll(!expandAll);
  };

  const filteredData = filterData();

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <CircularProgress />
        <Typography>Loading Jira hierarchy...</Typography>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchJiraHierarchy}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  // Render no data state
  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No Jira operational excellence data available.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Operational Excellence JIRA Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {lastUpdated && (
              <Typography variant="body2" color="textSecondary">
                Last updated: {formatDate(lastUpdated)}
              </Typography>
            )}
            <Typography variant="body2" color="textSecondary">•</Typography>
            <Link
              href="https://maersk-tools.atlassian.net/jira/plans/14153/scenarios/14331/timeline?vid=20105"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                color: '#1976d2',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              View in Jira
            </Link>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={expandAll ? <UnfoldLess /> : <UnfoldMore />}
            onClick={handleExpandAll}
          >
            {expandAll ? 'Collapse All' : 'Expand All'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={fetchJiraHierarchy}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by title or key..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: '#6c757d', mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="To Do">To Do</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Done">Done</MenuItem>
                <MenuItem value="Backlog">Backlog</MenuItem>
                <MenuItem value="On Hold">On Hold</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Assignee</InputLabel>
              <Select
                value={assigneeFilter}
                label="Assignee"
                onChange={(e) => setAssigneeFilter(e.target.value)}
              >
                <MenuItem value="all">All Assignees</MenuItem>
                {getUniqueAssignees().map(assignee => (
                  <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Hierarchy View */}
      {filteredData.length === 0 ? (
        <Alert severity="info">
          No items match the current filters. Try adjusting your search criteria.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredData.map(capability => {
            const capStats = getCapabilityStats(capability);
            const isCapabilityExpanded = expandedCapabilities.has(capability.key);

            return (
              <Paper key={capability.key} elevation={2}>
                <Accordion
                  expanded={isCapabilityExpanded}
                  onChange={() => toggleCapability(capability.key)}
                  sx={{ boxShadow: 'none' }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      bgcolor: '#f5f5f5',
                      '&:hover': { bgcolor: '#eeeeee' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                            {capability.title}
                          </Typography>
                          <Link 
                            href={getJiraUrl(capability.key)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            sx={{ 
                              color: '#1976d2',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {capability.key}
                          </Link>
                          <Chip
                            label={capability.status}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(capability.status) + ' !important',
                              color: getStatusTextColor(capability.status) + ' !important',
                              fontWeight: 500,
                              border: 'none'
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary">
                            {capStats.totalEpics} Epic{capStats.totalEpics !== 1 ? 's' : ''}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {capStats.totalIssues} Total Issue{capStats.totalIssues !== 1 ? 's' : ''}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, maxWidth: 300 }}>
                            <LinearProgress
                              variant="determinate"
                              value={capStats.progress}
                              sx={{
                                flex: 1,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: '#4caf50'
                                }
                              }}
                            />
                            <Typography variant="body2" color="textSecondary" sx={{ minWidth: 45 }}>
                              {capStats.progress}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {/* Epics and Issues in Table Format */}
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 600, width: 40 }}></TableCell>
                            <TableCell sx={{ fontWeight: 600, width: 120 }}>Key</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: 150 }}>Assignee</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: 140 }}>Start Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: 140 }}>Due Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: 140 }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {capability.epics.map(epic => {
                            const isEpicExpanded = expandedEpics.has(epic.key);
                            const epicOverdue = epic.due_date ? isOverdue(epic.due_date) : false;

                            return (
                              <React.Fragment key={epic.key}>
                                {/* Epic Row */}
                                <TableRow 
                                  sx={{ 
                                    bgcolor: '#fafafa',
                                    '&:hover': { bgcolor: '#f0f0f0' },
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => toggleEpic(epic.key)}
                                >
                                  <TableCell sx={{ pl: 2 }}>
                                    <IconButton size="small" sx={{ p: 0 }}>
                                      {isEpicExpanded ? <ExpandMore /> : <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>
                                    <Link 
                                      href={getJiraUrl(epic.key)} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      sx={{ 
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    >
                                      {epic.key}
                                    </Link>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {epic.title}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {epic.assignee ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Person sx={{ fontSize: 16, color: '#757575' }} />
                                        <Typography variant="body2">{epic.assignee}</Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="textSecondary">
                                        Unassigned
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="textSecondary">
                                      {epic.start_date ? formatDate(epic.start_date) : '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {epicOverdue && (
                                        <Warning sx={{ fontSize: 16, color: '#d32f2f' }} />
                                      )}
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          color: epicOverdue ? '#d32f2f' : 'textSecondary',
                                          fontWeight: epicOverdue ? 600 : 400
                                        }}
                                      >
                                        {epic.due_date ? formatDate(epic.due_date) : '-'}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={epic.status}
                                      size="small"
                                      sx={{
                                        bgcolor: getStatusColor(epic.status) + ' !important',
                                        color: getStatusTextColor(epic.status) + ' !important',
                                        fontWeight: 500,
                                        fontSize: '0.7rem',
                                        border: 'none'
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>

                                {/* Issues under Epic */}
                                {isEpicExpanded && epic.issues.map(issue => {
                                  const issueOverdue = isOverdue(issue.due_date);
                                  
                                  return (
                                    <TableRow
                                      key={issue.key}
                                      sx={{
                                        bgcolor: 'white',
                                        '&:hover': { bgcolor: '#f5f5f5' }
                                      }}
                                    >
                                      <TableCell sx={{ pl: 6 }}></TableCell>
                                      <TableCell>
                                        <Link 
                                          href={getJiraUrl(issue.key)} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          sx={{ 
                                            color: '#1976d2',
                                            textDecoration: 'none',
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            '&:hover': { textDecoration: 'underline' }
                                          }}
                                        >
                                          {issue.key}
                                        </Link>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">{issue.title}</Typography>
                                      </TableCell>
                                      <TableCell>
                                        {issue.assignee ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Person sx={{ fontSize: 16, color: '#757575' }} />
                                            <Typography variant="body2">{issue.assignee}</Typography>
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" color="textSecondary">
                                            Unassigned
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" color="textSecondary">
                                          {formatDate(issue.start_date)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          {issueOverdue && (
                                            <Warning sx={{ fontSize: 16, color: '#d32f2f' }} />
                                          )}
                                          <Typography
                                            variant="body2"
                                            sx={{ 
                                              color: issueOverdue ? '#d32f2f' : 'textSecondary', 
                                              fontWeight: issueOverdue ? 600 : 400 
                                            }}
                                          >
                                            {formatDate(issue.due_date)}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={issue.status}
                                          size="small"
                                          sx={{
                                            bgcolor: getStatusColor(issue.status) + ' !important',
                                            color: getStatusTextColor(issue.status) + ' !important',
                                            fontWeight: 500,
                                            fontSize: '0.7rem',
                                            border: 'none'
                                          }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
